import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './asset-downtimes.style.js';
import { getDowntimesByAsset } from '@cmms/api';
import { db } from '../../../../services/auth.service.js';

@customElement('asset-downtimes')
export class AssetDowntimes extends LitElement {
  @property({ type: Object }) asset: any = null;
  @property({ type: String }) companyId: string = '';

  @state() private downtimes: Array<any> = [];
  @state() private isLoading = false;
  @state() private error: string | null = null;

  static styles = styles;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadDowntimes();
  }

  async updated(changed: Map<string, unknown>) {
    if (changed.has('asset') && this.asset) {
      await this.loadDowntimes();
    }
  }

  private async loadDowntimes() {
    if (!this.asset) return;

    const ids = Array.from(new Set([
      this.asset?.id,
      this.asset?.asset_id,
      this.asset?.model?.asset_id
    ].filter(Boolean).map((v: any) => String(v))));

    if (ids.length === 0) {
      this.downtimes = [];
      return;
    }

    try {
      this.isLoading = true;
      this.error = null;
      const collected: any[] = [];

      for (const id of ids) {
        const resp: any = await getDowntimesByAsset(db, id);
        const raw = Array.isArray(resp) ? resp : (resp?.data || []);
        if (raw && raw.length) collected.push(...raw);
      }

      this.downtimes = collected;
    } catch (e: any) {
      this.error = e?.message || 'Failed to load downtimes';
      this.downtimes = [];
    } finally {
      this.isLoading = false;
    }
  }

  private formatDate(timestamp: number | null): string {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  }

  private formatDuration(startedAt: number, endedAt: number | null): string {
    if (!endedAt) return 'Ongoing';
    
    const duration = endedAt - startedAt;
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  private getStatusBadgeClass(endedAt: number | null): string {
    return endedAt ? 'status-badge completed' : 'status-badge active';
  }

  private getStatusText(endedAt: number | null): string {
    return endedAt ? 'Completed' : 'Active';
  }

  render() {
    if (this.isLoading) {
      return html`
        <div class="loading-container">
          <sl-spinner></sl-spinner>
          <span>Loading downtimes...</span>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="error-container">
          <sl-icon name="exclamation-triangle" class="error-icon"></sl-icon>
          <span>${this.error}</span>
        </div>
      `;
    }

    if (this.downtimes.length === 0) {
      return html`
        <div class="empty-state">
          <sl-icon name="clock" class="empty-icon"></sl-icon>
          <h3>No Downtimes</h3>
          <p>This asset has no recorded downtimes.</p>
        </div>
      `;
    }

    return html`
      <div class="downtimes-container">
        <div class="downtimes-header">
          <h3>Asset Downtimes (${this.downtimes.length})</h3>
        </div>

        <div class="data-table">
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Started</th>
                <th>Ended</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              ${this.downtimes.map(downtime => {
                const model = downtime.model || downtime;
                return html`
                  <tr class="clickable-row">
                    <td>
                      <span class="${this.getStatusBadgeClass(model.ended_at)}">
                        ${this.getStatusText(model.ended_at)}
                      </span>
                    </td>
                    <td>${this.formatDate(model.started_at)}</td>
                    <td>${this.formatDate(model.ended_at)}</td>
                    <td>${this.formatDuration(model.started_at, model.ended_at)}</td>
                  </tr>
                `;
              })}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }
}
